import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Form, FormField, Heading, Input, Modal, Select, Spinner, Table, Textarea } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import {
  Agent,
  FesConfig,
  TriggerToken,
  TriggerTokenChannel,
  TriggerTokenCreatedResponse,
  createTriggerToken,
  deleteTriggerToken,
  getAgents,
  getTriggerTokens,
} from '../../fes';
import { jiraProjectKeyPattern } from './teamForm';

type TriggerTokenFormState = {
  channel: TriggerTokenChannel;
  projectKey: string;
  defaultAgentId: string;
  allowedAgentIds: string[];
  description: string;
};

const emptyForm: TriggerTokenFormState = {
  channel: 'jira',
  projectKey: '',
  defaultAgentId: '',
  allowedAgentIds: [],
  description: '',
};

export function TriggerTokensPanel({
  fesConfig,
  teamId,
  tokens,
}: {
  fesConfig: FesConfig;
  teamId: string;
  tokens: AuthTokens;
}) {
  const [triggerTokens, setTriggerTokens] = useState<TriggerToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [areAgentsLoading, setAreAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string>();
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [form, setForm] = useState<TriggerTokenFormState>(emptyForm);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string>();
  const [generatedToken, setGeneratedToken] = useState<TriggerTokenCreatedResponse['data']>();
  const [copyStatus, setCopyStatus] = useState<string>();
  const [tokenToRevoke, setTokenToRevoke] = useState<TriggerToken | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string>();

  const loadTriggerTokens = useCallback(async () => {
    setIsLoading(true);
    setLoadError(undefined);

    try {
      const response = await getTriggerTokens(fesConfig, tokens, teamId);
      setTriggerTokens(response.data || []);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Unable to load trigger tokens.');
    } finally {
      setIsLoading(false);
    }
  }, [fesConfig, teamId, tokens]);

  useEffect(() => {
    void loadTriggerTokens();
  }, [loadTriggerTokens]);

  const loadAgents = async () => {
    if (agents.length > 0 || areAgentsLoading) {
      return;
    }

    setAreAgentsLoading(true);
    setAgentsError(undefined);

    try {
      const response = await getAgents(fesConfig, tokens);
      setAgents(response.data || []);
    } catch (error) {
      setAgentsError(error instanceof Error ? error.message : 'Unable to load agents.');
    } finally {
      setAreAgentsLoading(false);
    }
  };

  const handleGenerateOpen = () => {
    setForm(emptyForm);
    setGenerateError(undefined);
    setGeneratedToken(undefined);
    setCopyStatus(undefined);
    setIsGenerateModalOpen(true);
    void loadAgents();
  };

  const handleGenerateClose = () => {
    if (isGenerating) {
      return;
    }

    setIsGenerateModalOpen(false);
    setForm(emptyForm);
    setGenerateError(undefined);
    setGeneratedToken(undefined);
    setCopyStatus(undefined);
  };

  const handleGenerateResultClose = () => {
    handleGenerateClose();
    void loadTriggerTokens();
  };

  const handleFieldChange = (field: 'projectKey' | 'defaultAgentId' | 'description', value: string) => {
    setForm(currentForm => ({
      ...currentForm,
      [field]: field === 'projectKey' ? value.toUpperCase() : value,
    }));
    setGenerateError(undefined);
  };

  const handleChannelChange = (channel: TriggerTokenChannel) => {
    setForm(currentForm => ({
      ...currentForm,
      channel,
      projectKey: channel === 'jira' ? currentForm.projectKey : '',
    }));
    setGenerateError(undefined);
  };

  const handleAllowedAgentToggle = (agentId: string, checked: boolean) => {
    setForm(currentForm => ({
      ...currentForm,
      allowedAgentIds: checked
        ? Array.from(new Set([...currentForm.allowedAgentIds, agentId]))
        : currentForm.allowedAgentIds.filter(id => id !== agentId),
    }));
    setGenerateError(undefined);
  };

  const handleCreate = async () => {
    const projectKey = form.projectKey.trim().toUpperCase();

    if (form.channel === 'jira' && projectKey && !jiraProjectKeyPattern.test(projectKey)) {
      setGenerateError(
        'Project key must start with an uppercase letter and contain 2-10 uppercase letters, numbers, or underscores.'
      );
      return;
    }

    setIsGenerating(true);
    setGenerateError(undefined);

    try {
      const description = form.description.trim();
      const response = await createTriggerToken(fesConfig, tokens, teamId, {
        channel: form.channel,
        ...(form.channel === 'jira' && projectKey ? { projectKey } : {}),
        ...(form.defaultAgentId ? { defaultAgentId: form.defaultAgentId } : {}),
        ...(form.allowedAgentIds.length > 0 ? { allowedAgentIds: form.allowedAgentIds } : {}),
        ...(description ? { description } : {}),
      });

      if (!response.data?.token) {
        throw new Error('FES did not return a generated trigger token.');
      }

      setGeneratedToken(response.data);
    } catch (error) {
      setGenerateError(error instanceof Error ? error.message : 'Unable to generate trigger token.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateConfirm = () => {
    if (generatedToken) {
      handleGenerateResultClose();
      return;
    }

    void handleCreate();
  };

  const handleCopy = async () => {
    if (!generatedToken?.token) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      setCopyStatus('Clipboard API is not available.');
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedToken.token);
      setCopyStatus('Copied.');
    } catch {
      setCopyStatus('Unable to copy the token.');
    }
  };

  const handleRevokeClose = () => {
    if (isRevoking) {
      return;
    }

    setTokenToRevoke(null);
    setRevokeError(undefined);
  };

  const handleRevoke = async () => {
    if (!tokenToRevoke) {
      return;
    }

    setIsRevoking(true);
    setRevokeError(undefined);

    try {
      await deleteTriggerToken(fesConfig, tokens, teamId, tokenToRevoke.tokenId);
      setTokenToRevoke(null);
      await loadTriggerTokens();
    } catch (error) {
      setRevokeError(error instanceof Error ? error.message : 'Unable to revoke trigger token.');
    } finally {
      setIsRevoking(false);
    }
  };

  const projectKey = form.projectKey.trim().toUpperCase();
  const canGenerate =
    !isGenerating && (form.channel !== 'jira' || !projectKey || jiraProjectKeyPattern.test(projectKey));

  return (
    <>
      <section className="app-team-panel" aria-labelledby="trigger-tokens-heading">
        <div className="app-team-panel-header">
          <div>
            <div className="app-kicker">Team Detail</div>
            <Heading level={2} id="trigger-tokens-heading" className="app-panel-title">
              Trigger Tokens
            </Heading>
            <p className="app-panel-subtitle">Create scoped credentials for Jira, Azure DevOps, and pipeline automations.</p>
          </div>
          <div className="app-actions">
            <Button onClick={handleGenerateOpen} disabled={isLoading}>
              Generate trigger token
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="app-detail-loading" role="status">
            <Spinner center>Loading trigger tokens...</Spinner>
          </div>
        ) : loadError ? (
          <div className="app-result app-team-error" role="alert">
            {loadError}
          </div>
        ) : triggerTokens.length === 0 ? (
          <p className="app-empty">No trigger tokens found.</p>
        ) : (
          <div className="app-table-wrap">
            <Table className="app-table" colored>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Token ID</Table.Th>
                  <Table.Th>Channel</Table.Th>
                  <Table.Th>Project Key</Table.Th>
                  <Table.Th>Default Agent ID</Table.Th>
                  <Table.Th>Allowed Agent IDs</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created At</Table.Th>
                  <Table.Th>Created By</Table.Th>
                  <Table.Th>Revoked At</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {triggerTokens.map(triggerToken => {
                  const isRevoked = triggerToken.status === 'revoked';
                  const isCurrentTokenRevoking = isRevoking && tokenToRevoke?.tokenId === triggerToken.tokenId;

                  return (
                    <Table.Tr key={triggerToken.tokenId}>
                      <Table.Td>{triggerToken.tokenId}</Table.Td>
                      <Table.Td>{triggerToken.channel}</Table.Td>
                      <Table.Td>{triggerToken.projectKey || '-'}</Table.Td>
                      <Table.Td>{triggerToken.defaultAgentId || '-'}</Table.Td>
                      <Table.Td>{triggerToken.allowedAgentIds?.length ? triggerToken.allowedAgentIds.join(', ') : 'Any team agent'}</Table.Td>
                      <Table.Td>{triggerToken.description || '-'}</Table.Td>
                      <Table.Td>{triggerToken.status}</Table.Td>
                      <Table.Td>{triggerToken.createdAt}</Table.Td>
                      <Table.Td>{triggerToken.createdBy || '-'}</Table.Td>
                      <Table.Td>{triggerToken.revokedAt || '-'}</Table.Td>
                      <Table.Td>
                        <Button
                          ariaLabel={`Revoke trigger token ${triggerToken.tokenId}`}
                          className="app-table-icon-button"
                          disabled={isLoading || isRevoked || isCurrentTokenRevoking}
                          hiddenLabel
                          icon="semantic-delete"
                          incognito
                          onClick={() => {
                            setTokenToRevoke(triggerToken);
                            setRevokeError(undefined);
                          }}
                          small
                          title={isRevoked ? 'Trigger token already revoked' : `Revoke ${triggerToken.tokenId}`}
                        >
                          Revoke
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </section>

      {isGenerateModalOpen ? (
        <Modal
          buttonCancelLabel="Cancel generate trigger token"
          buttonCancelText={generatedToken ? undefined : 'Cancel'}
          buttonCloseLabel="Close"
          buttonConfirmLabel={generatedToken ? 'Close generated trigger token' : 'Generate trigger token'}
          buttonConfirmText={generatedToken ? 'OK' : isGenerating ? 'Generating...' : 'Generate'}
          confirmationDisabled={generatedToken ? false : !canGenerate}
          onCancel={generatedToken ? undefined : handleGenerateClose}
          onClose={generatedToken ? handleGenerateResultClose : handleGenerateClose}
          onConfirm={handleGenerateConfirm}
          shown
          status={generatedToken ? 'warning' : undefined}
          title={generatedToken ? 'Trigger token generated' : 'Generate trigger token'}
        >
          {generatedToken ? (
            <div className="app-webhook-key-result">
              <div className="app-webhook-key-warning" role="alert">
                This token will only be displayed this time and can't be retrieved later. Copy it before closing this dialog.
              </div>
              <FormField labelForId="generated-trigger-token" labelText="Generated token" type="textarea">
                <Textarea id="generated-trigger-token" value={generatedToken.token} readOnly rows={5} />
              </FormField>
              <div className="app-copy-row">
                <Button secondary onClick={() => void handleCopy()}>
                  Copy generated token
                </Button>
                {copyStatus ? (
                  <span className="app-copy-status" role="status">
                    {copyStatus}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {generateError ? (
                <div className="app-result app-team-error" role="alert">
                  {generateError}
                </div>
              ) : null}
              {agentsError ? (
                <div className="app-result app-team-error" role="alert">
                  {agentsError}
                </div>
              ) : null}
              <Form
                className="app-create-team-form"
                onSubmit={event => {
                  event.preventDefault();
                  void handleCreate();
                }}
              >
                <FormField labelForId="create-trigger-token-channel" labelText="Channel" notion type="select">
                  <Select
                    id="create-trigger-token-channel"
                    value={form.channel}
                    onChange={event => handleChannelChange(event.target.value as TriggerTokenChannel)}
                    disabled={isGenerating}
                  >
                    <Select.Item value="jira">Jira</Select.Item>
                    <Select.Item value="azure-devops">Azure DevOps</Select.Item>
                    <Select.Item value="pipeline">Pipeline</Select.Item>
                  </Select>
                </FormField>
                {form.channel === 'jira' ? (
                  <FormField labelForId="create-trigger-token-project-key" labelText="Project Key" type="input">
                    <Input
                      id="create-trigger-token-project-key"
                      value={form.projectKey}
                      onChange={event => handleFieldChange('projectKey', event.target.value)}
                      placeholder="Optional, e.g. PROJ"
                      readOnly={isGenerating}
                    />
                  </FormField>
                ) : null}
                <FormField labelForId="create-trigger-token-default-agent" labelText="Default Agent" type="select">
                  <Select
                    id="create-trigger-token-default-agent"
                    value={form.defaultAgentId}
                    onChange={event => handleFieldChange('defaultAgentId', event.target.value)}
                    disabled={areAgentsLoading || isGenerating}
                  >
                    <Select.Item value="">No default agent</Select.Item>
                    {agents.map(agent => (
                      <Select.Item key={agent.agentId} value={agent.agentId}>
                        {agent.name ? `${agent.name} (${agent.agentId})` : agent.agentId}
                      </Select.Item>
                    ))}
                  </Select>
                </FormField>
                <section className="app-trigger-token-agent-selector" aria-labelledby="allowed-trigger-token-agents-heading">
                  <div className="app-trigger-token-agent-selector-header">
                    <h3 id="allowed-trigger-token-agents-heading">Allowed Agents (optional)</h3>
                    <span>{form.allowedAgentIds.length} selected</span>
                  </div>
                  <p className="app-field-hint">Leave empty to allow any team agent.</p>
                  {areAgentsLoading ? (
                    <div className="app-detail-loading app-detail-loading--compact" role="status">
                      <Spinner center>Loading agents...</Spinner>
                    </div>
                  ) : (
                    <div className="app-trigger-token-agent-list" role="group" aria-labelledby="allowed-trigger-token-agents-heading">
                      {agents.map(agent => (
                        <Checkbox
                          key={agent.agentId}
                          id={`trigger-token-agent-${agent.agentId}`}
                          checked={form.allowedAgentIds.includes(agent.agentId)}
                          disabled={isGenerating}
                          onChange={event => handleAllowedAgentToggle(agent.agentId, event.target.checked)}
                        >
                          <span className="app-agent-skill-option">
                            <span className="app-agent-skill-name">{agent.name || agent.agentId}</span>
                            <span className="app-agent-skill-id">{agent.agentId}</span>
                          </span>
                        </Checkbox>
                      ))}
                      {agents.length === 0 ? <p className="app-empty">No agents found.</p> : null}
                    </div>
                  )}
                </section>
                <FormField labelForId="create-trigger-token-description" labelText="Description" type="textarea">
                  <Textarea
                    id="create-trigger-token-description"
                    value={form.description}
                    onChange={event => handleFieldChange('description', event.target.value)}
                    readOnly={isGenerating}
                    rows={3}
                  />
                </FormField>
              </Form>
            </>
          )}
        </Modal>
      ) : null}

      {tokenToRevoke ? (
        <Modal
          buttonCancelLabel="Cancel revoke trigger token"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={`Revoke trigger token ${tokenToRevoke.tokenId}`}
          buttonConfirmText={isRevoking ? 'Revoking...' : 'Revoke'}
          confirmationDisabled={isRevoking}
          onCancel={handleRevokeClose}
          onClose={handleRevokeClose}
          onConfirm={() => void handleRevoke()}
          shown
          status="warning"
          title="Revoke trigger token"
        >
          {revokeError ? (
            <div className="app-result app-team-error" role="alert">
              {revokeError}
            </div>
          ) : null}
          <p className="app-modal-copy">
            Revoke trigger token <strong>{tokenToRevoke.tokenId}</strong>? Automations using it will no longer be accepted.
          </p>
        </Modal>
      ) : null}
    </>
  );
}
